// Teacher → class roster. Each student shows completed-lessons + XP and links
// to their full progress dashboard. R9.

import { useT } from '@fajrarabi/i18n/react';
import { Badge, Card, CardBody, Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';

import { asLocale } from '~/lib/locale';
import { listClassStudents } from '~/lib/school';

export const Route = createFileRoute('/$locale/teacher/class/$classId')({
  component: ClassRosterScreen,
});

function ClassRosterScreen() {
  const t = useT();
  const { locale: rawLocale, classId } = useParams({ from: '/$locale/teacher/class/$classId' });
  const locale = asLocale(rawLocale);
  const students = useQuery({
    queryKey: ['teacher', 'class', classId, 'students'],
    queryFn: ({ signal }) => listClassStudents(classId, signal),
  });

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.teacher.roster')}</h1>

      <div className="mt-6 space-y-3">
        {students.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (students.data?.length ?? 0) === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-ink-700">{t('app.teacher.no_students')}</p>
            </CardBody>
          </Card>
        ) : (
          students.data!.map((s) => (
            <Link
              key={s.id}
              to="/$locale/teacher/student/$studentId"
              params={{ locale, studentId: s.id }}
              className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10 transition-shadow hover:shadow-[0_18px_40px_-22px_rgba(31,26,23,0.3)]"
            >
              <div>
                <p className="font-semibold text-ink-900">{s.displayName ?? s.username}</p>
                <p className="text-sm text-ink-500">@{s.username}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone="brand">
                  {t('app.teacher.lessons_done', { n: s.lessonsCompleted })}
                </Badge>
                <Badge tone="neutral">{t('app.kid.xp', { n: s.xp })}</Badge>
                <span className="text-sm font-medium text-burgundy-500">
                  {t('app.teacher.view_progress')} →
                </span>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
